import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import MailDetailPanel from "../mail/MailDetailPanel.jsx";
import MailClaimAssignment from "../mail/MailClaimAssignment.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";

export default function MailDetailModal({
  open,
  onClose,
  mailId,
  isIncoming,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [mail, setMail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  useEffect(() => {
    if (open) {
      setOpenCD(true);
    } else {
      setOpenCD(false);
      setMail(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !mailId) return;

    const endpoint = isIncoming
      ? `data/incoming-mails/${mailId}`
      : `data/outgoing-mails/${mailId}`;

    const params = isIncoming
      ? { include: "attachments" }
      : { api_version: "v2", include: "attachments,source" };

    backendGet(endpoint, {
      label: "E-Mail laden",
      enqueueSnackbar,
      options: { params },
      setIsLoading,
      setResult: (res) => setMail(res?.data ?? res),
    });
  }, [open, mailId, isIncoming, enqueueSnackbar]);

  useEffect(() => {
    const subject = mail?.subject ?? "E-Mail";

    setTca({
      title: subject,
      maxWidth: "lg",
      content: (
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
          {isLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4 }}>
              <CircularProgress size={20} />
              <Typography>Wird geladen…</Typography>
            </Box>
          )}
          {!isLoading && mail && (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MailDetailPanel mail={mail} isIncoming={isIncoming} />
              </Box>
              {isIncoming && (
                <Box
                  sx={{
                    minWidth: 250,
                    maxWidth: 300,
                    flexShrink: 0,
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                    border: "2px solid #f0f0f0",
                  }}
                >
                  <MailClaimAssignment mail={mail} />
                </Box>
              )}
            </>
          )}
        </Box>
      ),
      actions: (
        <DsButton variant="tertiary" onClick={onClose}>
          Schließen
        </DsButton>
      ),
    });
  }, [mail, isLoading, isIncoming, onClose]);

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      onClose={onClose}
      disableBackdropClick={false}
    />
  );
}

MailDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mailId: PropTypes.string,
  isIncoming: PropTypes.bool.isRequired,
};
